import { supabase } from './supabase';

/**
 * Where images live, and how a stored value becomes a URL.
 *
 * ── THE DECISION THIS FILE EXISTS FOR ──────────────────────────────────────
 * The database stores a storage KEY — `profile-photos/<uid>/<uuid>.jpg` — and
 * never a full URL. Every URL is built here, at read time.
 *
 * That one choice is what keeps the hosting decision reversible. Moving from
 * Supabase Storage to Bunny.net, S3+CloudFront or anything else becomes a
 * change to `mediaUrl` plus a base URL, instead of a migration that rewrites
 * every row and breaks every image already in the wild.
 *
 * Storing full URLs is the common mistake and it is close to irreversible:
 * once a few thousand rows contain `https://xyz.supabase.co/...`, the provider
 * is baked into the data.
 *
 * ── WHY SUPABASE STORAGE FOR NOW ───────────────────────────────────────────
 * It is already built (bucket and policies in migration 0013), the write path
 * is guarded by the same auth as everything else, and the free tier covers
 * development comfortably. It is S3-compatible, so a later move is mechanical.
 *
 * Worth knowing for when that decision comes up again:
 *
 *   AWS S3      — the free tier is 12 MONTHS only, and egress at ~$0.09/GB is
 *                 the most expensive of the three. Never serve images straight
 *                 from S3; it needs CloudFront in front, which is more moving
 *                 parts than we need today.
 *   Bunny.net   — genuinely the cheapest for bandwidth (~$0.01/GB, good India
 *                 edge presence) and the right answer IF bandwidth becomes a
 *                 real line item. It is a CDN, not an auth-aware store, so it
 *                 suits public assets — which profile photos are.
 *   Supabase    — bundled with what we already pay for, integrated with auth.
 *
 * The honest driver is not the provider, it is the payload: see `compress`
 * below. A 4 MB phone photo squeezed to ~120 KB makes every provider cheap and
 * makes the page fast on a 4G connection, which is most of this market.
 */

/**
 * Optional override. Point this at a CDN pulling from the storage bucket and
 * every image URL follows, with no other change.
 */
const MEDIA_BASE = import.meta.env.VITE_MEDIA_BASE_URL as string | undefined;

const BUCKET = 'profile-photos';

/**
 * Turn a stored value into something an `<img src>` can use.
 *
 * Three shapes are accepted on purpose:
 *   - a full URL      → returned as-is (external avatars, legacy rows)
 *   - a bare filename → a local static asset (the seeded demo portraits)
 *   - a storage key   → resolved against the CDN base or Supabase
 */
export function mediaUrl(value: string | null | undefined): string | undefined {
  if (!value) return undefined;

  if (value.startsWith('http://') || value.startsWith('https://')) return value;

  // No slash means it is not a storage key — it is one of the static portraits
  // in /public, which the Avatar component resolves by extension.
  if (!value.includes('/')) return value;

  if (MEDIA_BASE) return `${MEDIA_BASE.replace(/\/$/, '')}/${value}`;

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(stripBucket(value));
  return data.publicUrl;
}

/** Keys are stored with the bucket prefix; the SDK wants it without. */
const stripBucket = (key: string) =>
  key.startsWith(`${BUCKET}/`) ? key.slice(BUCKET.length + 1) : key;

/* ── Uploading ────────────────────────────────────────────────────────────── */

export class UploadError extends Error {}

/** Long side, in pixels. Beyond this nothing on the site renders any sharper. */
const MAX_EDGE = 1024;
const JPEG_QUALITY = 0.82;
const MAX_SOURCE_BYTES = 12 * 1024 * 1024;

/**
 * Resize and re-encode in the browser before uploading.
 *
 * This matters more than the choice of storage provider. A photo straight off
 * a phone is 3-8 MB; at 1024px and quality 0.82 the same picture is 100-150 KB.
 * That is roughly a 30x reduction in upload time on mobile data, in storage,
 * and in every byte served afterwards.
 *
 * It also sidesteps the bucket's 5 MB limit without ever showing the user a
 * "file too large" error for a photo their phone considers normal.
 *
 * EXIF is dropped as a side effect of canvas re-encoding, which is a privacy
 * win worth stating: phone photos carry GPS coordinates, and a companion's
 * home location must not travel with their profile picture.
 */
async function compress(file: File): Promise<Blob> {
  if (file.size > MAX_SOURCE_BYTES) {
    throw new UploadError('That image is very large. Try one under 12 MB.');
  }
  if (!file.type.startsWith('image/')) {
    throw new UploadError('That is not an image.');
  }

  const bitmap = await createImageBitmap(file).catch(() => {
    throw new UploadError('That image could not be read. Try a JPG or PNG.');
  });

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new UploadError('Could not process that image.');

  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );

  if (!blob) throw new UploadError('Could not process that image.');
  return blob;
}

/**
 * Upload one image and return its storage KEY — not a URL.
 *
 * The filename is a fresh UUID rather than anything derived from the user.
 * The bucket is public, so a guessable path would let someone enumerate every
 * companion's photos. A random key means holding the URL is the only way to
 * see the image, which is how every large platform handles public media.
 *
 * The `<uid>/` prefix is not decoration — the storage RLS policy in 0013
 * requires it to match `auth.uid()`, so nobody can write into anyone else's
 * folder.
 */
export async function uploadImage(userId: string, file: File): Promise<string> {
  const blob = await compress(file);
  const key = `${userId}/${crypto.randomUUID()}.jpg`;

  const { error } = await supabase.storage.from(BUCKET).upload(key, blob, {
    contentType: 'image/jpeg',
    cacheControl: '31536000', // immutable: the key changes when the image does
    upsert: false,
  });

  if (error) throw new UploadError(error.message);

  return `${BUCKET}/${key}`;
}

/**
 * Delete an image we previously uploaded.
 *
 * Failure is swallowed deliberately. This is called after the database has
 * already stopped pointing at the file, so a failed delete leaves an orphan
 * costing a fraction of a paisa — not worth showing the user an error about,
 * and not worth blocking their save. A sweeper job can collect orphans later.
 */
export async function deleteImage(key: string): Promise<void> {
  await supabase.storage
    .from(BUCKET)
    .remove([stripBucket(key)])
    .catch(() => undefined);
}
